import { memo, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import envs from '../common/envs';
import LoadingAnim from '../components/LoadingAnim';
import { AthenaServices } from '../lib/athena/athena-services';
import { customInstance } from '../v2/api/custom-instance';

// base64 디코딩 헬퍼 함수
function decodeBase64IfNeeded(value: string | null): string | null {
  if (!value) return null;

  const base64Pattern = /^[A-Za-z0-9+/=]+$/;
  if (base64Pattern.test(value) && value.length % 4 === 0) {
    try {
      return atob(value);
    } catch (error) {
      // 디코딩 실패 시 원래 값 사용
      return value;
    }
  }
  return value;
}

function QrRedirect() {
  const { uniqId } = useParams<{ uniqId?: string }>();
  const [searchParams] = useSearchParams();
  const aid = searchParams.get('aid');
  const eid = searchParams.get('eid');

  // uuid 파라미터 (createPreVisit으로 생성된 visit의 uuid)
  const uuidParam = searchParams.get('uuid');

  // Provider redirect 체크 완료 여부 (custom1이 없거나 체크 완료 시 true)
  const [providerCheckDone, setProviderCheckDone] = useState(false);

  // 여러 가능한 visit ID 파라미터 확인 (우선순위: custom1, appointmentId, aid, vid)
  const custom1Raw = searchParams.get('custom1');
  const appointmentIdRaw = searchParams.get('appointmentId');
  const aidRaw = searchParams.get('aid');
  const vidRaw = searchParams.get('vid');

  // base64 디코딩
  const custom1 = decodeBase64IfNeeded(custom1Raw);
  const appointmentId = decodeBase64IfNeeded(appointmentIdRaw);
  const aidDecoded = decodeBase64IfNeeded(aidRaw);
  const vid = decodeBase64IfNeeded(vidRaw);

  // visit ID 결정 (우선순위: custom1 > appointmentId > aid > vid)
  const visitId = custom1 || appointmentId || aidDecoded || vid;

  const [publicRedirect, setPublicRedirect] = useState<string | null>(null);
  const [publicRedirectLoading, setPublicRedirectLoading] = useState(false);
  const [providerRedirect, setProviderRedirect] = useState<string | null>(null);
  const [providerLoading, setProviderLoading] = useState(false);
  const [providerCalled, setProviderCalled] = useState(false);

  useEffect(() => {
    if (!uniqId) {
      return undefined;
    }

    let isMounted = true;
    setPublicRedirectLoading(true);

    customInstance<string | null>({
      url: `/deployments/public-redirect/${uniqId}`,
      method: 'GET',
    })
      .then((result) => {
        if (isMounted) {
          setPublicRedirect(result);
        }
      })
      .catch((error) => {
        console.error('QrRedirect: failed to fetch public redirect', error);
      })
      .finally(() => {
        if (isMounted) {
          setPublicRedirectLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [uniqId]);

  // uuid가 있으면 바로 설문 페이지로 이동 (createPreVisit으로 생성된 visit)
  // visitId(custom1, appointmentId, aid, vid 중 하나)가 있으면 provider redirect 시도, 없으면 바로 체크 완료
  // 단, EMR 연동(aid + eid)인 경우는 athena-check에서 처리하므로 provider redirect 체크 건너뜀
  useEffect(() => {
    // uuid가 있으면 provider redirect 체크 건너뛰고 바로 설문 페이지로
    if (uuidParam) {
      console.log('QrRedirect: uuid found, skipping provider redirect check');
      setProviderCheckDone(true);
      return;
    }

    // EMR 연동인 경우 provider redirect 체크 건너뛰고 바로 athena-check로
    if (aid && eid) {
      console.log('QrRedirect: EMR integration detected, skipping provider redirect check');
      setProviderCheckDone(true);
      return;
    }

    if (visitId && uniqId) {
      console.log('QrRedirect: visitId found, checking for existing patient', visitId);
      setProviderCalled(true);
      setProviderLoading(true);
      customInstance<string | null>({
        url: '/deployments/public-provider-redirect',
        method: 'GET',
        params: {
          confirmationCode: visitId,
          uniqId,
        },
      })
        .then((result) => {
          setProviderRedirect(result);
        })
        .catch((error) => {
          console.error('QrRedirect: provider redirect lookup failed', error);
        })
        .finally(() => {
          setProviderLoading(false);
        });
    } else if (!visitId || !uniqId) {
      // visitId 또는 uniqId가 없으면 바로 일반 redirect로 진행
      setProviderCheckDone(true);
    }
  }, [visitId, uniqId, aid, eid, uuidParam]);

  // Provider redirect 결과 처리 (EMR이 아닌 경우에만)
  useEffect(() => {
    // EMR 연동인 경우 이 로직 건너뜀 (위에서 이미 providerCheckDone = true)
    if (aid && eid) {
      return;
    }

    // visitId가 있고 쿼리가 호출되었고 로딩이 완료되었을 때만 처리
    if (!visitId || !providerCalled || providerLoading) {
      return;
    }

    const uuid = providerRedirect;
    if (uuid) {
      // provider report 페이지로 이동
      console.log('QrRedirect: found existing patient, redirecting to provider report', uuid);
      const providerReportLink = `${envs.surveyBase}/sp/${uuid}`;
      window.location.href = providerReportLink;
      return;
    }

    // uuid가 없으면 provider check 완료 → 일반 redirect로 진행
    console.log('QrRedirect: no existing patient found, proceeding to new survey');
    setProviderCheckDone(true);
  }, [visitId, providerCalled, providerLoading, providerRedirect, aid, eid]);

  // 메인 redirect - provider check가 완료된 후에만 실행
  useEffect(() => {
    // provider check가 완료되지 않았으면 대기
    if (!providerCheckDone) {
      return;
    }

    const checkStatusAndRedirect = async () => {
      if (publicRedirect) {
        // uuid가 있으면 설문 페이지로 uuid를 포함하여 이동 (createPreVisit으로 생성된 visit)
        if (uuidParam) {
          console.log('QrRedirect: redirecting to survey with uuid', uuidParam);
          // custom1도 있으면 함께 전달
          const params = new URLSearchParams();
          params.set('uuid', uuidParam);
          if (custom1) {
            params.set('custom1', btoa(custom1));
          }
          const link = `${envs.surveyBase}/s${publicRedirect}?${params.toString()}`;
          window.location.href = link;
          return;
        }

        // aid query parameter가 있으면 athena-info-check로 리다이렉트
        if (aid && eid) {
          try {
            const surveyStatus = await AthenaServices.checkSurveyStatusByAid(aid);

            if (surveyStatus) {
              if (surveyStatus.completed || surveyStatus.archived || surveyStatus.status === 'done') {
                console.log('Survey already completed/terminated, redirecting to terminated page');
                const terminatedLink = `${envs.surveyBase}/s${publicRedirect}/terminated`;
                window.location.href = terminatedLink;
                return;
              }
            }
          } catch (error) {
            console.error('Error checking survey status:', error);
          }

          const athenaLink = `${envs.surveyBase}/s${publicRedirect}/athena-check?aid=${aid}&eid=${eid}`;
          window.location.href = athenaLink;
          return;
        }

        // custom1이 있지만 uuid를 못 찾은 경우, custom1을 URL에 포함시켜서 설문 페이지로 이동
        if (custom1) {
          console.log('QrRedirect: redirecting to new survey with custom1', custom1);
          const link = `${envs.surveyBase}/s${publicRedirect}?custom1=${btoa(custom1)}`;
          window.location.href = link;
          return;
        }

        // 일반적인 경우 (aid만 있고 eid가 없으면 EMR 연동이 아니므로 aid 전달 안 함)
        const link = `${envs.surveyBase}/s${publicRedirect}`;
        window.location.href = link;
      }
    };

    checkStatusAndRedirect();
  }, [providerCheckDone, publicRedirect, aid, eid, custom1, uuidParam]);

  if (publicRedirectLoading || (visitId && !providerCheckDone)) {
    return (
      <div className='h-full flex-1 flex-center overflow-hidden'>
        <LoadingAnim />
      </div>
    );
  }

  return <div className='flex-col gap-4 w-full h-screen flex-center'>Redirecting ...</div>;
}

export default memo(QrRedirect);
